import {
  Component,
  ComponentRef,
  Injector,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioGroupComponent, NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { QuestionnairesService } from './questionnaires.service';
import { ManageSettingsService } from '../../../../manage-settings/manage-settings.service';
import {
  firstValueFrom,
  forkJoin,
  from,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import {
  allowedFileExtensions,
  ExtendedUploadFile,
  ManageQuestionnaireDrawerParams,
  QuestionAnswers,
  QuestionFile,
  QuestionnaireMode,
  QuestionnairesAnswerResponse,
  QuestionnairesPayload,
  QuestionnairesResponse,
} from './questionnaires.interface';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../core/services/drawer.service';
import {
  LinearScale,
  QuestionnaireResponse,
} from '../../../../manage-settings/manage-settings.interface';
import { CommonService } from '../../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../../core/models/error.models';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AnswerType } from '../../../../manage-settings/manage-settings.enums';
import { CommonModule } from '@angular/common';
import { LoaderService } from '../../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { format } from 'date-fns';
import { OrganizationService } from '../../../../organizations/organization.service';
import { PrintQuestionnairesFormComponent } from './print-questionnaires-form/print-questionnaires-form.component';
import { StudentAttachmentService } from '../student-attachment.service';
import { FileData } from './questionnaires.interface';
import { OrganizationModel } from '../../../../organizations/organization.interface';
import { ApiResponse } from '../../../../../core/models/api-response.models';
import { inRange, intersection, map } from 'lodash';

@Component({
  selector: 'app-questionnaires-form',
  standalone: true,
  imports: [
    NzFormModule,
    NzInputModule,
    NzDividerModule,
    NzRadioModule,
    NzCheckboxModule,
    NzRadioGroupComponent,
    NzUploadModule,
    NzButtonModule,
    SvgIconComponent,
    NzDatePickerModule,
    NzTimePickerModule,
    NzSelectModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSpinModule,
  ],
  templateUrl: './questionnaires-form.component.html',
  styleUrl: './questionnaires-form.component.scss',
})
export class QuestionnairesFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  caseParams?: ManageQuestionnaireDrawerParams =
    {} as ManageQuestionnaireDrawerParams;
  questionList: QuestionnaireResponse[] = [];
  questionAnswersList: QuestionnairesAnswerResponse[] = [];
  form!: FormGroup;
  answerType = AnswerType;
  loading$: Observable<boolean> = of(false);
  timeControls: string[] = [];
  fileControls: string[] = [];
  questionAnswerResponses = {} as QuestionnairesResponse;
  attachmentName?: string = '';
  organizationDetail = {} as OrganizationModel;
  uploadedFiles: QuestionFile[] = [];
  allowedFileExtensions = allowedFileExtensions;
  titlePattern = new RegExp('^\\S.*$');
  constructor(
    private manageSettingsService: ManageSettingsService,
    private questionnairesService: QuestionnairesService,
    private drawerService: DrawerService,
    private commonService: CommonService,
    private loaderService: LoaderService,
    private viewContainerRef: ViewContainerRef,
    private injector: Injector,
    private orgService: OrganizationService,
    private studentAttachmentService: StudentAttachmentService
  ) {
    this.loading$ = this.loaderService.loading$;
  }

  ngOnInit(): void {
    this.subscribeToDrawer();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToDrawer(): void {
    this.drawerService
      .getParams<ManageQuestionnaireDrawerParams>(
        DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(async params => {
        if (!params) return;
        this.caseParams = params;
        await this.loadQuestions();
        this.getOrganizationDetail();
      });
  }

  async getOrganizationDetail() {
    if (this.caseParams) {
      this.organizationDetail = await firstValueFrom(
        this.orgService
          .getOrganization(this.caseParams?.case?.student.organizationId || 0)
          .pipe(takeUntil(this.destroy$))
      );
    }
  }

  get trimAttachmentName(): string {
    return this.attachmentName?.trimStart().trimEnd() ?? '';
  }

  private async loadQuestions(): Promise<void> {
    if (!this.caseParams) return;
    const response = await this.safeApiCall(() =>
      this.manageSettingsService.getQuestionsByPlanType(
        this.caseParams?.planTypeId || 0
      )
    );
    if (!response) return;

    this.questionList = response.result;
    this.createForm();

    if (this.caseParams?.attachment) {
      await this.loadAnswers(this.caseParams.attachment.id);
    } else {
      this.loaderService.hide();
    }
  }

  private createForm(): void {
    const controls: Record<string, FormControl> = {};

    this.fileControls = [];
    this.timeControls = [];

    for (const q of this.questionList) {
      const controlName = `que_${q.id}`;
      switch (q.answerTypeId) {
        case this.answerType.CHECKBOX:
          controls[controlName] = new FormControl([], [Validators.required]);
          break;
        case this.answerType.FILEUPLOAD:
          this.fileControls.push(controlName);
          controls[controlName] = new FormControl([], [Validators.required]);
          break;
        case this.answerType.TIME:
          this.timeControls.push(controlName);
          controls[controlName] = new FormControl(null, [Validators.required]);
          break;
        case this.answerType.TEXTBOX:
          controls[controlName] = new FormControl(null, [
            Validators.required,
            Validators.maxLength(255),
            Validators.pattern(this.titlePattern),
          ]);
          break;
        case this.answerType.PARAGRAPH:
          controls[controlName] = new FormControl(null, [
            Validators.required,
            Validators.maxLength(1000),
            Validators.pattern(this.titlePattern),
          ]);
          break;
        default:
          controls[controlName] = new FormControl('', [Validators.required]);
      }
    }

    this.form = new FormGroup(controls);
  }

  private async loadAnswers(attachmentId: number): Promise<void> {
    const response = await this.safeApiCall(() =>
      this.questionnairesService.getQuestionsResponseByAttachmentId(
        attachmentId
      )
    );
    if (!response) return;

    this.questionAnswersList = response.result;
    this.setFormAnswers();
  }

  private async setFormAnswers(): Promise<void> {
    for (const question of this.questionList) {
      const control = this.form.get(`que_${question.id}`);
      if (!control) continue;
      if (this.questionAnswersList.length > 0) {
        this.attachmentName = this.questionAnswersList[0].questionnaireName;
      }

      const answer = this.questionAnswersList.find(
        a => a.questionId === question.id
      );
      let value = null;
      if (answer) {
        switch (Number(answer.answerType)) {
          case this.answerType.CHECKBOX: {
            const answerValue = this.stringToArray(answer.answerValue);
            value = intersection(answerValue, map(question.options, 'id'));
            break;
          }
          case this.answerType.RADIOBUTTON: {
            const answerValue = Number(answer.answerValue);
            value = question.options.some(opt => opt.id === answerValue)
              ? answerValue
              : '';
            break;
          }
          case this.answerType.LINEARSCALE: {
            const answerValue = Number(answer.answerValue);
            value = inRange(
              answerValue,
              Number(question.linearScale?.minScaleValue) || 0,
              Number(question.linearScale?.maxScaleValue)
            )
              ? answerValue
              : null;

            break;
          }
          case this.answerType.DROPDOWN: {
            const answerValue = Number(answer.answerValue);
            value = question.options.some(opt => opt.id === answerValue)
              ? answerValue
              : '';
            break;
          }
          case this.answerType.TEXTBOX || this.answerType.PARAGRAPH:
            value = answer.answerValue;
            break;

          case this.answerType.DATE:
            value = new Date(answer.answerValue);
            break;

          case this.answerType.TIME:
            value = this.convertToDate(answer.answerValue);
            break;

          case this.answerType.FILEUPLOAD:
            value =
              answer?.files.length > 0
                ? await this.getFilesFromUrl(answer.files)
                : [];
            break;

          default:
            value = answer.answerValue ?? null;
            break;
        }
      } else {
        value = null;
      }

      control.setValue(value);
    }
    this.loaderService.hide();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || !this.caseParams) {
      Object.keys(this.form.controls).forEach(key => {
        this.getControl(key)?.markAsTouched();
      });
      return;
    }
    const responses = this.buildResponses();
    const payload = this.buildPayload(responses);

    const response = await this.safeApiCall(() =>
      this.questionnairesService.SaveOrUpdateQuestionResponse(payload)
    );
    if (!response) return;

    this.questionAnswerResponses = response.result;
    await this.handleUploadsAndAttachment(response.result);
  }

  private buildResponses(): QuestionAnswers[] {
    return this.questionList.map(q => {
      const value = this.form.get(`que_${q.id}`)?.value;

      switch (q.answerTypeId) {
        case AnswerType.TIME:
          return {
            questionId: q.id,
            answerText: value ? format(value, 'HH:mm') : '',
          };
        case AnswerType.CHECKBOX:
          return {
            questionId: q.id,
            answerText: '',
            selectedOptionIds: value ?? [],
          };
        case AnswerType.FILEUPLOAD:
          return { questionId: q.id, answerText: '' };
        default:
          return { questionId: q.id, answerText: value };
      }
    });
  }

  private buildPayload(responses: QuestionAnswers[]): QuestionnairesPayload {
    return {
      caseId: this.caseParams?.case?.id || 0,
      planTypeId: this.caseParams?.planTypeId || 1,
      caseAttachmentId:
        this.caseParams?.mode === QuestionnaireMode.EDIT
          ? this.caseParams.attachment?.id
          : undefined,
      attachmentName: this.attachmentName,
      responses,
    };
  }

  private async handleUploadsAndAttachment(result: QuestionnairesResponse) {
    const fileUploads = this.prepareFileUploads(result);

    if (fileUploads.length > 0) {
      const filesResponses = await this.uploadFiles(fileUploads);
      const allFiles = filesResponses.flatMap(res => res.result);
      this.uploadedFiles = allFiles ?? [];
    }

    await this.prepareQuestionnaireAttachment();
  }

  private prepareFileUploads(result: QuestionnairesResponse) {
    return this.fileControls
      .map(ctrl => {
        const questionId = Number(ctrl.split('_')[1]);
        const files = this.form.get(ctrl)?.value;
        if (files?.length) {
          return {
            questionId,
            caseAttachmentId: result.caseAttachmentId,
            files,
          };
        }
        return null;
      })
      .filter(Boolean) as {
      questionId: number;
      caseAttachmentId: number;
      files: ExtendedUploadFile[];
    }[];
  }

  private async uploadFiles(
    fileList: {
      questionId: number;
      caseAttachmentId: number;
      files: ExtendedUploadFile[];
    }[]
  ): Promise<ApiResponse<QuestionFile[]>[]> {
    try {
      this.loaderService.show();
      const uploadRequests = fileList.map(item =>
        this.questionnairesService.uploadQuestionnaireDocuments(
          item.caseAttachmentId,
          item.questionId,
          item.files
        )
      );
      const responses = await firstValueFrom(
        forkJoin(uploadRequests).pipe(takeUntil(this.destroy$))
      );
      return responses;
    } catch (err) {
      this.commonService.notify('error', 'File upload failed.');
      this.loaderService.hide();
      console.error(err);
      throw err;
    }
  }

  private async prepareQuestionnaireAttachment(): Promise<void> {
    const factory = this.viewContainerRef.createComponent(
      PrintQuestionnairesFormComponent,
      { injector: this.injector }
    );
    factory.location.nativeElement.style.display = 'none';

    try {
      const logoUrl =
        this.organizationDetail?.logoUrl || '/assets/images/logo.png';
      const base64Image = await firstValueFrom(
        from(this.commonService.getBase64ImageFromUrl(logoUrl)).pipe(
          takeUntil(this.destroy$)
        )
      );

      factory.instance.questionnaireData = {
        questionnairesData: this.questionAnswerResponses.responses,
        logo: base64Image,
        attachmentName: this.questionAnswerResponses.attachmentName ?? '',
        caseDetail: this.caseParams?.case,
        orgDetail: this.organizationDetail,
        uploadedFiles: this.uploadedFiles ?? [],
      };
      await this.waitForComponent(factory);
      factory.instance.printPDF();

      await new Promise<void>((resolve, reject) => {
        factory.instance.questionnariePdf
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (file: File) => {
              this.updateAttachment(file);
              factory.destroy();
              resolve();
            },
            error: err => {
              factory.destroy();
              reject(err);
            },
          });
      });
    } catch (err) {
      console.error('Attachment preparation failed', err);
    }
  }

  private async updateAttachment(file: File): Promise<void> {
    const response = await this.safeApiCall(() =>
      this.studentAttachmentService.uploadAttachment(
        this.caseParams?.case?.id || 0,
        0,
        this.caseParams?.planTypeId || 0,
        this.attachmentName ?? '',
        'Questionnaire Document',
        '1',
        file,
        this.questionAnswerResponses.caseAttachmentId || 0,
        true
      )
    );

    if (response) {
      this.commonService.notify(
        'success',
        'Questionnaries Document Generated Successfully'
      );
      await this.closeDrawer();
    } else {
      console.error('Upload failed or returned no data');
    }
  }

  private waitForComponent(
    factory: ComponentRef<PrintQuestionnairesFormComponent>
  ) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }

  getBeforeUploadFn(questionId: number) {
    return (file: NzUploadFile): boolean => {
      if (!file) return false;
      const MAX_FILE_SIZE = 5 * 1024 * 1024;
      if (file.size && file.size > MAX_FILE_SIZE) {
        this.commonService.notify('error', `File "${file.name}" exceeds 5MB`);
        return false;
      }
      const control = this.form.controls['que_' + questionId] as FormControl<
        ExtendedUploadFile[]
      >;
      const existingFiles = control.value || [];

      const newFile: ExtendedUploadFile = {
        ...file,
        uid: file.uid || String(Date.now()),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        name: file.name || (file as any).originFileObj?.name || 'Unnamed file',
        status: 'done',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        originFileObj: file as any,
        fileId: undefined,
        url: '',
      };

      if (file.type && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          newFile.url = reader.result as string;
          control.setValue([...existingFiles, newFile]);
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        reader.readAsDataURL(file as any);
      } else {
        control.setValue([...existingFiles, newFile]);
      }

      return false;
    };
  }

  handlePreview = (file: NzUploadFile): void => {
    if (!file) return;
    const extFile = file as ExtendedUploadFile;
    let fileUrl: string | undefined;

    if (extFile.url) {
      fileUrl = extFile.url;
    } else if (extFile.thumbUrl) {
      fileUrl = extFile.thumbUrl;
    } else if (extFile.originFileObj) {
      fileUrl = URL.createObjectURL(extFile.originFileObj as File);
    }
    if (!fileUrl) {
      return;
    }
    window.open(fileUrl, '_blank');

    if (extFile.originFileObj && !extFile.url) {
      setTimeout(() => URL.revokeObjectURL(fileUrl!), 1000);
    }
  };

  getRemoveFileFn(questionId: number) {
    return (file: NzUploadFile): boolean => {
      const control = this.form.controls['que_' + questionId] as FormControl<
        NzUploadFile[]
      >;
      control.setValue(control.value.filter(f => f.uid !== file.uid));
      return true;
    };
  }

  async getFilesFromUrl(fileList: FileData[]): Promise<ExtendedUploadFile[]> {
    const files = await Promise.all(
      fileList.map(async file => {
        const response = await fetch(file.fileUrl);
        const blob = await response.blob();
        const type = blob.type;
        const newFile = new File([blob], file.displayName, { type });

        return {
          uid: String(file.id),
          name: file.displayName,
          status: 'done',
          url: file.fileUrl,
          originFileObj: newFile,
          fileId: file.id,
        } as ExtendedUploadFile;
      })
    );

    return files;
  }

  getLinearOptions(linearScale?: LinearScale) {
    if (linearScale && linearScale.minScaleValue && linearScale.maxScaleValue) {
      return Array.from(
        { length: linearScale.maxScaleValue - linearScale.minScaleValue + 1 },
        (_, i) => linearScale.minScaleValue + i
      );
    } else {
      return [];
    }
  }

  onCheckboxChange(ctrl: string, checked: boolean, value: number): void {
    this.getControl(ctrl)?.markAsTouched();
    const selectedOptions = this.form.get(ctrl)?.value || [];
    if (checked) {
      selectedOptions.push(value);
    } else {
      const index = selectedOptions.indexOf(value);
      if (index > -1) {
        selectedOptions.splice(index, 1);
      }
    }
    this.form.get(ctrl)?.setValue(selectedOptions);
  }

  onDateChange(date: Date, questionId: number): void {
    const control = this.form.get('que_' + questionId) as FormControl;
    if (!(date instanceof Date)) return;
    const formatted = format(date, 'yyyy-MM-dd');
    if (control.value !== formatted) {
      control.setValue(formatted, { emitEvent: false });
    }
  }

  getControl(controlName: string) {
    return this.form.get(controlName);
  }

  private async safeApiCall<T>(fn: () => Observable<T>): Promise<T | null> {
    try {
      this.loaderService.show();
      return await firstValueFrom(fn().pipe(takeUntil(this.destroy$)));
    } catch (err) {
      this.handleError(err as ErrorResponse);
      this.loaderService.hide();
      return null;
    }
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const messages = errors?.map(e => e.description || e.detailedMessage) || [];
    this.commonService.notify('error', messages, message);
  }

  stringToArray(value: string): number[] {
    return value
      ? value
          .split(',')
          .map(v => Number(v.trim()))
          .filter(Boolean)
      : [];
  }

  convertToDate(timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number);
    const currentDate = new Date();
    currentDate.setHours(hours, minutes, 0, 0);

    return currentDate;
  }

  closeDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE);
  }
}
